import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

import { Bar } from "react-chartjs-2";
import dayjs from "dayjs";
import api from "../api/axios";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export default function ProjectMeterageChart() {
  const [loading, setLoading] = useState(true);
  const [navigation, setNavigation] = useState(null);
  const [projects, setProjects] = useState([]);
  const [chartData, setChartData] = useState([]);

  const fetchData = async (month = "") => {
    try {
      setLoading(true);

      const { data } = await api.get("/dashboard/project-meterage", {
        params: month ? { month } : {},
      });

      setNavigation(data.navigation);
      setProjects(data.projects || []);
      setChartData(data.chart_data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const data = {
    labels: (chartData || []).map((d) => d.day),

    datasets: (projects || []).map((project) => ({
      label: project.name,
      data: (chartData || []).map((d) => d[project.id] ?? 0),

      backgroundColor: project.color,
      borderColor: project.color,

      borderRadius: 5,
      maxBarThickness: 20,

      stack: "meterage",
    })),
  };

  const options = {
    responsive: true,

    maintainAspectRatio: false,

    interaction: {
      mode: "index",

      intersect: false,
    },

    plugins: {
      legend: {
        position: "top",

        labels: {
          color: "#fff",

          usePointStyle: true,

          pointStyle: "circle",
        },
      },

      tooltip: {
        backgroundColor: "#222",
        titleColor: "#fff",
        bodyColor: "#fff",

        callbacks: {
          title: (tooltipItems) => {
            if (!tooltipItems.length) return "";

            const index = tooltipItems[0].dataIndex;
            const row = chartData[index];

            if (!row) return "";

            const total = projects.reduce(
              (sum, project) => sum + Number(row[project.id] ?? 0),
              0,
            );

            return `${dayjs(row.date).format("DD MMM")} - ${total.toFixed(2)} m`;
          },
        },
      },
    },

    scales: {
      x: {
        stacked: true,
        ticks: {
          color: "#ddd",
        },

        grid: {
          color: "rgba(255,255,255,0.06)",
        },
      },

      y: {
        stacked: true,
        beginAtZero: true,

        ticks: {
          color: "#ddd",
        },

        title: {
          display: true,

          text: "Meterage",

          color: "#ddd",
        },

        grid: {
          color: "rgba(255,255,255,0.08)",
        },
      },
    },
  };

  return (
    <Box
      sx={{
        mt: 3,
        p: 3,
        borderRadius: 3,
        backdropFilter: "blur(12px)",
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      {/* Header */}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <IconButton
          disabled={!navigation?.prev_month}
          onClick={() => fetchData(navigation.prev_month)}
        >
          <ArrowBackIcon />
        </IconButton>

        <Typography variant="h6" fontWeight={600} textAlign="center">
          Daily Drilling Progress
          <Typography component="div" variant="body2" color="text.secondary">
            {navigation?.display_name}
          </Typography>
        </Typography>

        <IconButton
          disabled={!navigation?.next_month}
          onClick={() => fetchData(navigation.next_month)}
        >
          <ArrowForwardIcon />
        </IconButton>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            height: 420,
          }}
        >
          <Bar data={data} options={options} />
        </Box>
      )}
    </Box>
  );
}
